// services/authClient.ts
import { createAuthClient } from 'better-auth/react';
import { adminClient, twoFactorClient } from 'better-auth/client/plugins';
import { passkeyClient } from '@better-auth/passkey/client';

import { getBackendUrl } from '../config/backendUrl';

/** Where ora-be mounts Better Auth. Sessions are httpOnly cookies set here. */
export const AUTH_BASE_PATH = '/api/auth';

const buildAuthClient = (baseURL: string) =>
  createAuthClient({
    baseURL,
    basePath: AUTH_BASE_PATH,
    fetchOptions: { credentials: 'include' },
    plugins: [
      // The services that start a sign-in see `twoFactorRedirect` in their own
      // response and route to the 2FA page (TOTP only).
      twoFactorClient(),
      passkeyClient(),
      adminClient(),
    ],
  });

export type OraAuthClient = ReturnType<typeof buildAuthClient>;

let cachedClient: { baseURL: string; client: OraAuthClient } | null = null;

/**
 * The Better Auth client for the current backend. `createAuthClient` freezes
 * its base URL, so it is built per backend origin rather than once at module
 * load: the ORA-573 `?be=` override then applies without a reload, exactly as
 * it does for axios.
 */
export const getAuthClient = (): OraAuthClient => {
  const baseURL = getBackendUrl();
  if (!cachedClient || cachedClient.baseURL !== baseURL) {
    cachedClient = { baseURL, client: buildAuthClient(baseURL) };
  }
  return cachedClient.client;
};

/** The error half of every Better Auth client call. */
export interface AuthClientError {
  code?: string;
  message?: string;
  status?: number;
  statusText?: string;
}

/** `{ data, error }`, as every Better Auth client call resolves. */
export interface AuthClientResult<T> {
  data: T | null;
  error: AuthClientError | null;
}

/** Absolute URL of a Better Auth route on the current backend. */
export const getAuthUrl = (path: string): string =>
  `${getBackendUrl()}${AUTH_BASE_PATH}${path}`;
